/**
 * Builds a complete CDN image URL with width, height, fit, and quality params.
 *
 * Constructs a URL in the same format as `archiproImageLoader` but with
 * additional height and fit parameters for server-side aspect-ratio cropping.
 *
 * URL format: /images/cdn-images/{params}/{normalized-src}
 *
 * @see {@link packages/rocco/src/components/image/loaders.ts} archiproImageLoader
 */

interface CdnResizeOptions {
    width: number;
    height: number;
    fit?: 'cover' | 'contain' | 'scale-down' | 'crop' | 'pad';
    quality?: number;
}

/**
 * Builds a CDN image URL with width, height, and fit params so the
 * image service returns the image at the exact dimensions needed.
 *
 * Use with the Image component's `overrideSrc` prop to bypass the
 * standard width-only loader.
 *
 * @param src - The original image src (e.g., `/images/s1/product/photo.jpg/base64edits`).
 * @param options - Resize options: width, height, fit mode, and quality.
 * @returns A complete CDN URL ready for use as `overrideSrc`.
 */
export function buildCdnResizeUrl(
    src: string,
    options: CdnResizeOptions,
): string {
    if (!src) return src;

    const params = [
        `width=${options.width}`,
        `height=${options.height}`,
        `fit=${options.fit ?? 'cover'}`,
        `quality=${options.quality ?? 80}`,
    ];

    const paramsString = encodeURIComponent(params.join(','));
    const normalizedSrc = src[0] === '/' ? src.slice(1) : src;

    return `/images/cdn-images/${paramsString}/${normalizedSrc}`;
}
